import React from 'react';
import { Control, FieldValues, Path, useFormState, useWatch } from 'react-hook-form';
import StatusChip from '../StatusChip/StatusChip';
import { subtractArrays } from '../utils/util';

/**
 * Props for the StatusFormChip component
 * @template T - The form field values type extending FieldValues
 */
interface StatusFormChipProps<T extends FieldValues> {
  /** React Hook Form control object to manage form state */
  control: Control<T>;
  /** Array of form field names to monitor for changes and validation */
  fields: Path<T>[];
  /** Optional array of field names that must be valid (no errors) and filled (non-empty) for the form to be considered "Ready" */
  requiredFields?: Path<T>[];
}

/**
 * Pre-configured status chip components for different form states
 * @property {JSX.Element} toDo - Blue chip indicating no fields have been modified
 * @property {JSX.Element} inProgress - Info chip indicating some fields have been modified
 * @property {JSX.Element} ready - Green chip indicating all required fields are valid and filled
 * @property {JSX.Element} complete - Green chip indicating section is complete (same styling as ready)
 * @property {JSX.Element} error - Red chip indicating validation errors exist
 */
export const formChips = {
  toDo: <StatusChip size="small" variant="subtle" color="gray" label="To Do" />,
  inProgress: <StatusChip size="small" variant="subtle" color="info" label="In Progress" />,
  ready: <StatusChip size="small" variant="subtle" color="success" label="Ready" />,
  complete: <StatusChip size="small" variant="subtle" color="success" label="Complete" />,
  error: <StatusChip size="small" variant="subtle" color="error" label="Error" />,
};

/**
 * A form status indicator component that displays different status chips based on form state.
 *
 * This component monitors specified form fields and returns a status chip reflecting the current state:
 * - **Error**: When any monitored field has validation errors
 * - **Ready**: When all required fields are valid (no errors) and filled (non-empty values), or no required fields specified
 * - **In Progress**: When some (but not all) monitored fields have been modified
 * - **To Do**: When no fields have been modified yet
 *
 * @template T - The form field values type extending FieldValues from react-hook-form
 *
 * @param {StatusFormChipProps<T>} props - Component props
 * @param {Control<T>} props.control - React Hook Form control object
 * @param {Path<T>[]} props.fields - Array of field paths to monitor
 * @param {Path<T>[]} [props.requiredFields=[]] - Optional array of required field paths
 *
 * @returns {JSX.Element} A StatusChip component reflecting the current form state
 *
 * @example
 * // Basic usage with required fields
 * <StatusFormChip
 *   control={control}
 *   fields={['name', 'email', 'phone']}
 *   requiredFields={['name', 'email']}
 * />
 *
 * @example
 * // Usage without required fields (shows ready when all fields are filled)
 * <StatusFormChip
 *   control={control}
 *   fields={['address', 'city', 'zipCode']}
 * />
 */
const StatusFormChip = <T extends FieldValues>({ control, fields, requiredFields = [] }: StatusFormChipProps<T>) => {
  const { dirtyFields, errors } = useFormState({ control, name: fields });
  const requiredFieldValues = useWatch({ control, name: requiredFields });
  const dirties = Object.keys(dirtyFields);
  const errorKeys = Object.keys(errors);

  if (subtractArrays(fields, errorKeys).length !== fields.length) return formChips.error;

  if (requiredFields.length) {
    // Watch required field values to check if they're filled

    // Check if all required fields are valid (no errors) and have non-empty values
    const allRequiredFieldsValid = subtractArrays(requiredFields, errorKeys).length === requiredFields.length;
    const allRequiredFieldsHaveValues = Array.isArray(requiredFieldValues)
      ? requiredFieldValues.every((value) => {
          // Check for empty values (undefined, null, empty string, empty array)
          if (value === undefined || value === null || value === '') return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        })
      : requiredFieldValues !== undefined && requiredFieldValues !== null && requiredFieldValues !== '';

    if (allRequiredFieldsValid && allRequiredFieldsHaveValues) return formChips.ready;
  } else {
    return formChips.ready;
  }

  if (subtractArrays(fields, dirties).length < fields.length) return formChips.inProgress;

  return formChips.toDo;
};

export default StatusFormChip;
